import {
  AccountHierarchyResponseTypeEnum,
  IAccountRespSchema,
  MCustomSelectProps,
} from '@monetize/types/app';
import React, { useMemo, useState } from 'react';
import { useGetAccountHierarchyById } from '../../api/accountsService';
import { convertNestedAccountHierarchyToList } from '../../utils/account';
import { MBox, MText } from '../Monetize';
import { MCustomSelectTabItem } from '../Monetize/MCustomSelect/MCustomSelectTabItem';
import { AccountHierarchySelect } from './AccountHierarchySelect';
import { AccountSelect } from './AccountSelect';

interface AccountSelectWithHierarchyProps extends MCustomSelectProps {
  /** Root account whose hierarchy is shown by default */
  rootAccountId: string;
  value?: string;
  /** Always called with the selected account id */
  onChange: (accountId: string) => void;
  onClearAccountValue?: () => void;
}

/**
 * Account picker that defaults to the account hierarchy (indented tree) and lets the
 * user switch to a flat search of every active account.
 *
 * The switch lives in tabs at the top of the open dropdown rather than as a link
 * under the input: it is a scoping choice you make while browsing the list, and
 * below the input it read as permanent chrome on every row of a form.
 *
 * If the currently selected account is not part of the hierarchy (e.g. an existing
 * quote referencing an out-of-hierarchy account), it automatically renders the flat
 * all-accounts view so the account name still displays correctly.
 */
export const AccountSelectWithHierarchy = React.forwardRef<
  any,
  AccountSelectWithHierarchyProps
>(
  (
    {
      rootAccountId,
      value,
      onChange,
      onClearAccountValue,
      isReadOnly,
      isDisabled,
      ...rest
    }: AccountSelectWithHierarchyProps,
    ref,
  ) => {
    const { data: accountHierarchyResponse } = useGetAccountHierarchyById(
      rootAccountId,
      AccountHierarchyResponseTypeEnum.NESTED,
      { enabled: !!rootAccountId },
    );

    const hierarchyAccountIds = useMemo(() => {
      if (!accountHierarchyResponse) {
        return new Set<string>();
      }
      return new Set(
        convertNestedAccountHierarchyToList(accountHierarchyResponse).map(
          ({ id }) => id,
        ),
      );
    }, [accountHierarchyResponse]);

    const [manualShowAll, setManualShowAll] = useState(false);
    /**
     * Switching scope swaps which of the two selects is mounted, which tears down
     * the open dropdown with it. The replacement mounts already open so the switch
     * reads as changing what the list shows rather than dismissing it.
     * `isSelectOpen` is an initial-state prop, so this only takes effect on the
     * mount that a switch causes — the first render stays closed.
     */
    const [openOnMount, setOpenOnMount] = useState(false);

    const switchScope = (showAllAccounts: boolean) => {
      setManualShowAll(showAllAccounts);
      setOpenOnMount(true);
    };

    // Once the hierarchy has loaded, a selected account missing from it forces the
    // flat all-accounts view so its name resolves and displays.
    const isValueOutsideHierarchy =
      !!value && !!accountHierarchyResponse && !hierarchyAccountIds.has(value);
    const showAll = manualShowAll || isValueOutsideHierarchy;
    const canToggle = !isReadOnly && !isDisabled;

    // An out-of-hierarchy selection pins the flat view, so offering the tabs
    // would present a choice that can't be acted on until the value is cleared.
    const renderScopeTabs =
      canToggle && !isValueOutsideHierarchy
        ? () => (
            <MBox
              h="8"
              display="flex"
              borderBottom="1px"
              borderBottomColor="tGray.back"
            >
              <MCustomSelectTabItem
                active={!showAll}
                onClick={() => switchScope(false)}
                text="Hierarchy"
              />
              <MCustomSelectTabItem
                active={showAll}
                onClick={() => switchScope(true)}
                text="All Accounts"
              />
            </MBox>
          )
        : undefined;

    if (showAll) {
      return (
        <MBox w="full">
          <AccountSelect
            ref={ref}
            value={value}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            onClearAccountValue={onClearAccountValue}
            onChange={(account: IAccountRespSchema) =>
              onChange(account?.id ?? '')
            }
            renderPreContent={renderScopeTabs}
            isSelectOpen={openOnMount}
            {...rest}
          />
          {isValueOutsideHierarchy && (
            <MText color="fg.muted" fontSize="xs" mt="1">
              This account does not belong to the hierarchy.
            </MText>
          )}
        </MBox>
      );
    }

    return (
      <MBox w="full">
        <AccountHierarchySelect
          ref={ref}
          rootAccountId={rootAccountId}
          value={value}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
          onClearAccountValue={onClearAccountValue}
          onChange={onChange}
          renderPreContent={renderScopeTabs}
          isSelectOpen={openOnMount}
          {...rest}
        />
      </MBox>
    );
  },
);
